import { useState } from 'react';

import {
  ArrowRight,
  BarChart3,
  Calendar,
  Cloud,
  Database,
  GraduationCap,
  Key,
  Lock,
  Puzzle,
  Rocket,
  Settings,
  Target,
  Users,
  Zap,
} from 'lucide-react';
import { motion } from 'motion/react';

import { DiagramContainer } from '@src/components/DiagramContainer';
import AnswerSection from '@src/components/seo/AnswerSection';
import { Button } from '@src/components/ui/button';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

const BOSSS_BLUE = '#2973a3'; // Updated to match other pages
const BG_COLOR = '#F8FAFC';
const TEXT_COLOR = '#384656';
const DARK_TEXT = '#1C2534'; // For labels

// Helper function for precise line connections in circular layout
const calculateCircularPosition = (
  centerX,
  centerY,
  radius,
  angle,
) => {
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle),
  };
};

// Helper function to calculate line endpoints that touch circle circumference
const calculateLineEndpoints = (
  x1,
  y1,
  r1,
  x2,
  y2,
  r2,
) => {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  return {
    x1: x1 + r1 * Math.cos(angle),
    y1: y1 + r1 * Math.sin(angle),
    x2: x2 - r2 * Math.cos(angle),
    y2: y2 - r2 * Math.sin(angle),
  };
};

// Integration Systems Circular Diagram
function IntegrationSystemsCircularDiagram() {
  const [hoveredSystem, setHoveredSystem] = useState(null);

  const centerX = 200;
  const centerY = 160; // Moved down to center better in taller viewBox
  const radius = 105; // Optimized for even spacing while keeping circles within bounds
  const centralRadius = 48; // Keep central circle size
  const serviceRadius = 46; // Scaled up by ~12% for better proportion

  const systems = [
    {
      label: 'Databases',
      color: '#10B981',
      icon: Database,
      angle: -Math.PI / 2, // Top
    },
    {
      label: 'HR Systems',
      color: '#F59E0B',
      icon: Users,
      angle: -Math.PI / 2 + (2 * Math.PI) / 5, // Top-right
    },
    {
      label: 'Identity Providers',
      color: '#EF4444',
      icon: Key,
      angle: -Math.PI / 2 + (4 * Math.PI) / 5, // Bottom-right
    },
    {
      label: 'Cloud Platforms',
      color: '#8B5CF6',
      icon: Cloud,
      angle: -Math.PI / 2 + (6 * Math.PI) / 5, // Bottom-left
    },
    {
      label: 'ITSM Tools',
      color: BOSSS_BLUE,
      icon: Settings,
      angle: -Math.PI / 2 + (8 * Math.PI) / 5, // Top-left
    },
  ];

  const systemPositions = systems.map((system) => ({
    ...system,
    ...calculateCircularPosition(
      centerX,
      centerY,
      radius,
      system.angle,
    ),
  }));

  return (
    <DiagramContainer className="w-full max-w-xl mx-auto">
      <svg viewBox="0 0 400 320" className="w-full h-auto">
        {/* Central Hub */}
        <motion.circle
          cx={centerX}
          cy={centerY}
          r={centralRadius}
          fill={BOSSS_BLUE}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            filter: 'drop-shadow(0 6px 20px rgba(41, 115, 163, 0.4))',
          }}
        />

        <motion.text
          x={centerX}
          fill="white"
          fontSize="13"
          y={centerY - 6}
          fontWeight="bold"
          textAnchor="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          BOSSS
        </motion.text>

        <motion.text
          x={centerX}
          fill="white"
          fontSize="11"
          y={centerY + 8}
          fontWeight="bold"
          textAnchor="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          Integration
        </motion.text>

        {/* Connection lines from center border to system borders */}
        {systemPositions.map((system, i) => {
          const endpoints = calculateLineEndpoints(
            centerX,
            centerY,
            centralRadius,
            system.x,
            system.y,
            serviceRadius,
          );
          return (
            <motion.line
              key={`line-${i}`}
              x1={endpoints.x1}
              y1={endpoints.y1}
              x2={endpoints.x2}
              y2={endpoints.y2}
              strokeWidth="2.5"
              stroke={BOSSS_BLUE}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
            />
          );
        })}

        {/* System Nodes - Even distribution with perfect internal alignment */}
        {systemPositions.map((system, i) => {
          const IconComponent = system.icon;
          return (
            <g key={i}>
              <motion.circle
                fill="white"
                cx={system.x}
                cy={system.y}
                r={serviceRadius}
                strokeWidth="1.5"
                stroke={system.color}
                initial={{ scale: 0 }}
                animate={{
                  scale: hoveredSystem === system.label ? 1.05 : 1,
                }}
                transition={{
                  duration: 0.5,
                  delay: 0.7 + i * 0.1,
                }}
                style={{
                  filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))',
                  cursor: 'pointer',
                }}
                onMouseLeave={() => setHoveredSystem(null)}
                onMouseEnter={() => setHoveredSystem(system.label)}
              />

              {/* System icon - Centered above label with consistent positioning */}
              <motion.foreignObject
                width="24"
                height="20"
                x={system.x - 12}
                y={system.y - 22}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ pointerEvents: 'none' }}
                transition={{
                  duration: 0.3,
                  delay: 1.0 + i * 0.1,
                }}
              >
                <div className="w-6 h-5 flex items-center justify-center">
                  <IconComponent
                    className="w-5 h-5"
                    style={{
                      color: system.color,
                      strokeWidth: '1.5px',
                    }}
                  />
                </div>
              </motion.foreignObject>

              {/* System label - Perfectly contained with consistent padding */}
              <motion.foreignObject
                width="76"
                height="32"
                y={system.y + 6}
                x={system.x - 38}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ pointerEvents: 'none' }}
                transition={{
                  duration: 0.3,
                  delay: 1.2 + i * 0.1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    height: '100%',
                    fontSize: '10px',
                    fontWeight: '500',
                    color: DARK_TEXT,
                    lineHeight: '1.2',
                    padding: '3px 4px',
                    overflow: 'hidden',
                  }}
                >
                  {system.label}
                </div>
              </motion.foreignObject>
            </g>
          );
        })}
      </svg>
    </DiagramContainer>
  );
}

function SectionIntro({ title, text }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-6">
        Integration Services
      </div>

      <motion.h1
        viewport={{ once: true }}
        style={{ color: DARK_TEXT }}
        transition={{ duration: 0.8 }}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        className="text-4xl lg:text-5xl font-bold mb-4"
      >
        {title}
      </motion.h1>

      <motion.p
        viewport={{ once: true }}
        style={{ color: TEXT_COLOR }}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        className="text-xl max-w-3xl mx-auto"
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        {text}
      </motion.p>
    </div>
  );
}

const steps = [
  {
    icon: Calendar,
    title: '1. Discovery & Planning',
    description: 'We work with your team to understand your existing systems, data flows, and integration goals. This phase includes a detailed assessment of your current infrastructure and a roadmap for a successful integration.',
  },
  {
    icon: Target,
    title: '2. Custom Development',
    description: 'Our experts develop secure, reliable, and scalable integration solutions using APIs and custom scripts to ensure seamless data exchange between BOSSS and your third-party applications.',
  },
  {
    icon: Puzzle,
    title: '3. Testing & Validation',
    description: 'We rigorously test all integrations to ensure data accuracy, integrity, and security. We perform end-to-end testing with your team to validate that the connections meet all functional requirements.',
  },
  {
    icon: GraduationCap,
    title: '4. Training & Deployment',
    description: 'Once testing is complete, we assist with a smooth deployment. We also provide comprehensive training for your team on how to manage and monitor the new integrations within the BOSSS platform.',
  },
];

export function IntegrationServicesPage() {
  const onNavigateToTop = useNavigateToTop();
  return (
    <div className="min-h-screen" style={{ backgroundColor: BG_COLOR }} >
      {/* Hero Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Text Content */}
            <motion.div
              className="space-y-8"
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-6">
                  Integration Services
                </div>

                <h1 style={{ color: DARK_TEXT }} className="text-4xl lg:text-5xl font-bold">
                  Connect Everything. Simplify Security.
                </h1>

                <p style={{ color: TEXT_COLOR }} className="text-xl leading-relaxed">
                  Seamlessly connect BOSSS with your existing IT infrastructure,
                  third-party applications, and business-critical systems.
                  Our experts handle the complexity, so you can focus on
                  your security posture.
                </p>
              </div>

              <motion.div
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Button
                  style={{ backgroundColor: BOSSS_BLUE, color: 'white' }}
                  className="px-8 py-4 text-lg rounded-lg font-semibold transition-all duration-200 hover:shadow-xl"
                  onClick={() => {
                    onNavigateToTop('/demo');
                  }}
                >
                  Schedule a Consultation
                </Button>
              </motion.div>
            </motion.div>

            {/* Right Column - Diagram */}
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: 50 }}
            >
              <IntegrationSystemsCircularDiagram />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/services/integration" />


      {/* Why Integrate Section */}
      <section className="py-20 lg:py-32" style={{ backgroundColor: 'white' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            title="Why Integrate with BOSSS?"
            text="Maximize the value of your existing security investments and achieve a truly unified security and compliance platform."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-col items-center text-center p-6"
            >
              <Zap className="w-12 h-12 mb-4" style={{ color: BOSSS_BLUE }} />
              <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">Automate Workflows</h3>

              <p className="text-sm" style={{ color: TEXT_COLOR }}>
                Automate data synchronization, incident response, and compliance checks.
              </p>
            </motion.div>

            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col items-center text-center p-6"
            >
              <BarChart3 className="w-12 h-12 mb-4" style={{ color: BOSSS_BLUE }} />
              <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">Gain Deeper Insights</h3>

              <p className="text-sm" style={{ color: TEXT_COLOR }}>
                Unify data from multiple sources for a holistic view of your security posture.
              </p>
            </motion.div>

            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col items-center text-center p-6"
            >
              <Rocket className="w-12 h-12 mb-4" style={{ color: BOSSS_BLUE }} />
              <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">Accelerate Compliance</h3>

              <p className="text-sm" style={{ color: TEXT_COLOR }}>
                Streamline compliance reporting and audits with automated data collection.
              </p>
            </motion.div>

            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col items-center text-center p-6"
            >
              <Lock className="w-12 h-12 mb-4" style={{ color: BOSSS_BLUE }} />
              <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">Enhance Security</h3>

              <p className="text-sm" style={{ color: TEXT_COLOR }}>
                Ensure data integrity and security across all integrated systems.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionIntro
            title="Our Four-Step Integration Process"
            text="We follow a proven methodology to ensure your integrations are seamless, secure, and successful from start to finish."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                style={{ backgroundColor: 'white' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex flex-col items-center text-center p-6 rounded-xl border border-gray-200"
              >
                <div
                  style={{ backgroundColor: `${BOSSS_BLUE}1a` }} // 1a = ~10% opacity in hex
                  className="relative w-16 h-16 rounded-full flex items-center justify-center mb-4"
                >
                  <div
                    className="absolute w-12 h-12 rounded-full"
                    style={{ backgroundColor: `${BOSSS_BLUE}0d` }} // 0d = ~5% opacity
                  />

                  <div
                    className="absolute w-8 h-8 rounded-full"
                    style={{ backgroundColor: `${BOSSS_BLUE}1a` }}
                  />

                  <div
                    style={{ backgroundColor: `${BOSSS_BLUE}33` }} // 33 = ~20% opacity
                    className="relative z-10 p-2 rounded-full flex items-center justify-center"
                  >
                    <step.icon className="w-6 h-6" style={{ color: BOSSS_BLUE }} />
                  </div>
                </div>

                <h3 style={{ color: DARK_TEXT }} className="text-lg font-bold mb-2">{step.title}</h3>
                <p className="text-sm" style={{ color: TEXT_COLOR }}>{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section
        className="py-20 lg:py-32"
      >
        <div className="absolute inset-0" style={{ backgroundColor: BOSSS_BLUE, opacity: 0.05 }} />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2
              style={{ color: TEXT_COLOR }}
              className="text-3xl lg:text-4xl font-bold mb-6"
            >
              Ready to Connect Your Systems?
            </h2>

            <p
              className="text-xl mb-8 max-w-2xl mx-auto"
              style={{ color: TEXT_COLOR, opacity: 0.8 }}
            >
              Let our integration experts help you create
              seamless connections between BOSSS and your
              existing infrastructure. Contact us today to
              discuss your integration requirements.
            </p>

            <motion.div
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.05 }}
            >
              <Button
                style={{ backgroundColor: BOSSS_BLUE }}
                className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                onClick={() => onNavigateToTop('/contact')}
              >
                Start Your Integration Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
export default IntegrationServicesPage;
